import { Badge, Box, Text } from '@chakra-ui/react'
import moment from 'moment'
import React from 'react'
import { AlmaIcon } from '../../../icons/calendar/alma-icon'
import { GCalIcon } from '../../../icons/calendar/gcal-icon'
import { HeadwayIcon } from '../../../icons/calendar/headway-icon'
import { ICalIcon } from '../../../icons/calendar/ical-icon'
import { OutlookIcon } from '../../../icons/calendar/outlook-icon'
import { SimplePracticeIcon } from '../../../icons/calendar/simple-practice-icon'
import { TelehealthIcon } from '../../../icons/calendar/telehealth-icon'
import { TherapyNotesIcon } from '../../../icons/calendar/therapy-notes-icon'
import { EVENT_SETTING_TELEHEALTH } from '../models'
import { calendarEventStyles } from './calendar-event.styles'
import { useIsTablet } from './full-size-calendar'

function formatEventTime(start: string, end: string): string {
  const startTime = moment(start)
  const endTime = moment(end)

  const formatTime = (time: moment.Moment) => {
    const minutes = time.minutes()
    const hour = time.format('h')
    return minutes === 0 ? hour : `${hour}:${time.format('mm')}`
  }

  const startFormatted = formatTime(startTime)
  const endFormatted = formatTime(endTime)

  // If they're in the same meridiem period
  if (startTime.format('a') === endTime.format('a')) {
    return `${startFormatted} – ${endFormatted} ${endTime.format('a')}`
  }

  // If they cross meridiem periods
  return `${startFormatted} ${startTime.format(
    'a'
  )} – ${endFormatted} ${endTime.format('a')}`
}

export function CalendarEventCard({
  event,
  isSelected,
  onSelect,
  time,
  name,
  duration,
  isSessionOver,
  isMobile = false,
  isMonthView = false
}: {
  event: any
  isSelected: boolean
  onSelect: (e: React.MouseEvent) => void
  time: string
  name: string
  duration: number
  isSessionOver: boolean
  isMobile?: boolean
  isMonthView?: boolean
}) {
  const isGoogle =
    event.extendedProps && event.extendedProps.sourceCalendarType === 'google'
  const isIcal =
    event.extendedProps && event.extendedProps.sourceCalendarType === 'ical'
  const isOutlook =
    event.extendedProps && event.extendedProps.sourceCalendarType === 'outlook'
  const sourceCalendarType = event.extendedProps?.sourceCalendarType

  //build the className for each event type
  const baseClass = 'calendar-event-card'
  const stateClass = isSessionOver ? 'session-over-event' : 'confirmed-event'
  const selectedClass = isSelected ? 'selected-event' : ''

  //therapy app icon mapping
  const therapyCalendarIcons: Record<string, React.ReactNode> = {
    headway: <HeadwayIcon width={12} height={12} />,
    alma: <AlmaIcon width={12} height={12} />,
    simplepractice: <SimplePracticeIcon width={12} height={12} />,
    therapynotes: <TherapyNotesIcon width={12} height={12} />
  }

  const isTherapyApp =
    sourceCalendarType &&
    Object.hasOwn(therapyCalendarIcons, sourceCalendarType)

  const isTablet: boolean = useIsTablet()

  const getEventContentStyle = () => {
    if (isMobile) {
      return calendarEventStyles.eventContentMobile
    }
    if (duration <= 15 && !isTablet) {
      return calendarEventStyles.eventContentShort
    }
    return isTablet && duration <= 15
      ? calendarEventStyles.eventContentTabletShort
      : calendarEventStyles.eventContent
  }

  if (isGoogle) {
    const googleClass =
      `${baseClass} google-event-card ${stateClass} ${selectedClass}`.trim()
    return (
      <Box
        className={googleClass}
        sx={calendarEventStyles.googleEventCard}
        onClick={onSelect}
        position="relative"
        minHeight={isTablet ? 25 : undefined}
      >
        {!isMobile && (
          <Box position="absolute" top="2px" right="6px" zIndex={3}>
            <GCalIcon width={12} height={12} />
          </Box>
        )}
        <Box sx={getEventContentStyle()}>
          <Text
            className="event-title"
            sx={calendarEventStyles.googleEventTitle}
            title={name}
            isTruncated
          >
            {name}
          </Text>
          <Text className="event-time" sx={calendarEventStyles.googleEventTime}>
            {formatEventTime(event.start, event.end)}
          </Text>
        </Box>
      </Box>
    )
  }

  if (isIcal) {
    const icalClass =
      `${baseClass} ical-event-card ${stateClass} ${selectedClass}`.trim()
    return (
      <Box
        className={icalClass}
        sx={calendarEventStyles.icalEventCard}
        onClick={onSelect}
        position="relative"
        minHeight={isTablet ? 25 : undefined}
      >
        {!isMobile && (
          <Box position="absolute" top="2px" right="6px" zIndex={3}>
            <ICalIcon width={12} height={12} />
          </Box>
        )}
        <Box sx={getEventContentStyle()}>
          <Text
            className="event-title"
            sx={calendarEventStyles.icalEventTitle}
            title={name}
            isTruncated
          >
            {name}
          </Text>
          <Text className="event-time" sx={calendarEventStyles.icalEventTime}>
            {formatEventTime(event.start, event.end)}
          </Text>
        </Box>
      </Box>
    )
  }

  if (isOutlook) {
    const outlookClass =
      `${baseClass} outlook-event-card ${stateClass} ${selectedClass}`.trim()
    return (
      <Box
        className={outlookClass}
        sx={calendarEventStyles.outlookEventCard}
        onClick={onSelect}
        position="relative"
        minHeight={isTablet ? 25 : undefined}
      >
        {!isMobile && (
          <Box position="absolute" top="2px" right="6px" zIndex={3}>
            <OutlookIcon width={12} height={12} />
          </Box>
        )}
        <Box sx={getEventContentStyle()}>
          <Text
            className="event-title"
            sx={calendarEventStyles.outlookEventTitle}
            title={name}
            isTruncated
          >
            {name}
          </Text>
          <Text
            className="event-time"
            sx={calendarEventStyles.outlookEventTime}
          >
            {formatEventTime(event.start, event.end)}
          </Text>
        </Box>
      </Box>
    )
  }

  if (isTherapyApp) {
    const therapyClass = `${baseClass} ${stateClass} ${selectedClass}`.trim()
    return (
      <Box
        className={therapyClass}
        sx={calendarEventStyles.calendarEventCard}
        onClick={onSelect}
        position="relative"
        minHeight={isTablet ? 25 : undefined}
      >
        {!isMobile && (
          <Box position="absolute" top="2px" right="6px" zIndex={3}>
            {therapyCalendarIcons[sourceCalendarType]}
          </Box>
        )}
        <Box sx={getEventContentStyle()}>
          <Text
            className="event-title"
            sx={calendarEventStyles.eventTitle}
            title={name}
            isTruncated
          >
            {name}
          </Text>
          <Text className="event-time" sx={calendarEventStyles.eventTime}>
            {formatEventTime(event.start, event.end)}
          </Text>
        </Box>
      </Box>
    )
  }

  //default (Blueprint/internal) event
  const cardClass = [baseClass, stateClass, selectedClass].join(' ').trim()
  const isTelehealth = event.extendedProps?.setting === EVENT_SETTING_TELEHEALTH
  const telehealthIconBox =
    !isMobile && isTelehealth ? (
      <Box
        position="absolute"
        top={duration <= 15 && !isTablet ? '0px' : '2px'}
        right="6px"
        zIndex={3}
      >
        <TelehealthIcon
          width={duration <= 15 && !isTablet ? 12 : 16}
          height={duration <= 15 && !isTablet ? 12 : 16}
        />
      </Box>
    ) : null
  let badgeBox = null
  if (!isTelehealth && sourceCalendarType && !isMobile) {
    badgeBox = (
      <Badge colorScheme="blue" ml={2} textTransform="capitalize">
        {sourceCalendarType}
      </Badge>
    )
  }

  return (
    <Box
      className={cardClass}
      sx={calendarEventStyles.calendarEventCard}
      onClick={onSelect}
      position="relative"
      minHeight={isTablet ? 25 : undefined}
    >
      {telehealthIconBox}
      <Box sx={getEventContentStyle()}>
        <Box display="flex" flexDirection="column" width="100%" minWidth={0}>
          <Box sx={calendarEventStyles.eventRow}>
            <Text
              className="event-title"
              sx={calendarEventStyles.eventTitle}
              title={name}
              isTruncated
            >
              {name}
              {badgeBox}
            </Text>
          </Box>
          {duration > 15 && (
            <Text className="event-time" sx={calendarEventStyles.eventTime}>
              {formatEventTime(event.start, event.end)}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  )
}
